/**
 * api/stepPrompts.js — query/mutation para a tela "Prompts das Jornadas".
 *
 * Espelha a estrutura de `api/stepTokens.js` (mesmo cliente http, mesmo padrão
 * de invalidation), mas para o PROMPT DE SISTEMA base de cada step (em vez do
 * teto de tokens).
 *
 * Endpoints:
 *   GET /settings/step-prompts   — prompt-base por step (perm settings:view)
 *   PUT /settings/step-prompts   — ajusta overrides por step (perm igual à do
 *                                  Budget/Orçamento e Tokens por Step: requer
 *                                  Admin ou Manager — settings:edit)
 *
 * Resposta esperada (GET e PUT, mesmo shape):
 *   {
 *     steps: Array<{
 *       key: string, label: string, phase: string,
 *       default_text: string, override: string|null, effective: string,
 *     }>,
 *   }
 *
 * Body do PUT:
 *   { steps: Array<{ key: string, prompt_text: string|null }> }
 *   null (ou string vazia) remove o override e volta ao default do código.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

const QUERY_KEY = ['settings', 'step-prompts'];

/**
 * useStepPrompts — lê o prompt-base por step (default do código + overrides).
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useStepPrompts() {
  const api = useApi();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get('/settings/step-prompts'),
    staleTime: 60_000,
  });
}

/**
 * useSaveStepPrompts — grava os overrides de prompt_text por step (PUT).
 * Requer a mesma permissão de escrita usada pelo card de Orçamento/Tokens
 * por Step (Admin/Manager). Invalida a query no sucesso para refletir o
 * `effective` recalculado pelo backend.
 */
export function useSaveStepPrompts() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (steps) => api.put('/settings/step-prompts', { steps }),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(QUERY_KEY, data);
      } else {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      }
    },
  });
}
